/*
 * Наружные выдвижные ящики внизу шкафа (замечание Сергея 21.09, 2:17):
 * «пенал 1200: внизу три выдвижных со своими фасадами, выше шкаф сам».
 *
 * Ящики стоят в каждом пролёте корпуса между стойками. Над ними —
 * неподвижная полка; распашные двери начинаются над ящиками, внутреннее
 * наполнение шкафа — над этой полкой.
 */
import { CARCASS_MM } from "./carcass";
import { handleForFacade } from "../catalog";
import type { FacadeColorType, FacadePatternType, FacadeType, HandleType, WardrobeModule } from "../types";
import { cabinetBaseHeightMm } from "./installation";
import { cabinetSectionWidths } from "./moduleRules";

export const CABINET_DRAWER_MAX_COUNT = 4;
/** Фасад ящика не ниже 250 мм: направляющая и дно съедают часть высоты (правило цеха). */
export const CABINET_DRAWER_MIN_FRONT_MM = 250;
export const CABINET_DRAWER_MAX_FRONT_MM = 450;
export const CABINET_DRAWER_DEFAULT_FRONT_MM = 250;
/**
 * Зазор над каждым фасадом ящика — между фасадами и между верхним ящиком и
 * дверью. Ручек у цокольных ящиков нет: зазор 20 мм — зацеп, палец заходит за
 * верхнюю кромку фасада (видео Сергея 31.10). Раньше был шов 3 мм и ручки.
 */
export const CABINET_DRAWER_SEAM_MM = 20;
/** Цокольные ящики открываются за верхнюю кромку фасада — ручек нет. */
export const CABINET_DRAWERS_FINGER_PULL = true;
/** Над ящиками должно остаться место под дверь и наполнение. */
export const CABINET_MIN_DOOR_ABOVE_DRAWERS_MM = 400;
const PANEL_MM = CARCASS_MM;
/** Фасад на 2 мм выше низа корпуса — как у дверей. */
const FRONT_BOTTOM_REVEAL_MM = 2;

export const supportsCabinetDrawers = (module: Pick<WardrobeModule, "type">) =>
  module.type === "single" || module.type === "double";

export const cabinetDrawerFrontHeightMm = (module: Pick<WardrobeModule, "cabinetDrawerHeight">) =>
  Math.round(Math.min(CABINET_DRAWER_MAX_FRONT_MM, Math.max(CABINET_DRAWER_MIN_FRONT_MM,
    Number.isFinite(module.cabinetDrawerHeight) ? module.cabinetDrawerHeight! : CABINET_DRAWER_DEFAULT_FRONT_MM)));

/** Сколько ящиков помещается при такой высоте фасада, чтобы над ними осталась дверь. */
export const maxCabinetDrawerCount = (module: WardrobeModule, frontHeight = cabinetDrawerFrontHeightMm(module)) => {
  if (!supportsCabinetDrawers(module)) return 0;
  const free = module.height - cabinetBaseHeightMm(module) - CABINET_MIN_DOOR_ABOVE_DRAWERS_MM;
  return Math.max(0, Math.min(CABINET_DRAWER_MAX_COUNT, Math.floor((free + CABINET_DRAWER_SEAM_MM) / (frontHeight + CABINET_DRAWER_SEAM_MM))));
};

export const cabinetDrawerCount = (module: WardrobeModule) => {
  const requested = Math.round(Number(module.cabinetDrawers ?? 0));
  if (!Number.isFinite(requested) || requested <= 0) return 0;
  return Math.min(requested, maxCabinetDrawerCount(module));
};

/** Id «ячейки» ящиков пролёта: из него сцена собирает ключи выдвижения `drawer:<id>:<n>`. */
export const cabinetDrawerCellId = (sectionIndex: number) => `cabinet-${sectionIndex}`;

export interface CabinetDrawerFinish {
  facade: FacadeType;
  facadePattern?: FacadePatternType;
  facadeColor?: FacadeColorType;
  facadeColorHex?: string;
  handle: HandleType;
}

/**
 * Фасады наружных ящиков — как у дверей шкафа. Но стекло и зеркало на ящик
 * не ставят (то же правило, что у комода): под стеклянной или зеркальной
 * дверью ящики плитные — ЛДСП в декоре фасадов, с ручкой, подходящей ЛДСП.
 */
export const cabinetDrawerFinish = (module: WardrobeModule): CabinetDrawerFinish => {
  if (module.facade !== "glass" && module.facade !== "mirror") {
    return {
      facade: module.facade,
      facadePattern: module.facadePattern,
      facadeColor: module.facadeColor,
      facadeColorHex: module.facadeColorHex,
      handle: module.handle
    };
  }
  return { facade: "wood", facadePattern: "plain", handle: handleForFacade(module.handle, "wood") };
};

/** Ящики отделаны иначе, чем двери (дверь — стекло или зеркало). */
export const cabinetDrawerFinishDiffers = (module: WardrobeModule) =>
  module.facade === "glass" || module.facade === "mirror";

export interface CabinetDrawerBay {
  sectionIndex: number;
  /** Фасады в пролёте: края по оси X от центра корпуса, мм. */
  frontLeft: number;
  frontRight: number;
  /** Проём пролёта между боковиной и стойкой (или двумя стойками), мм. */
  innerLeft: number;
  innerRight: number;
}

export interface CabinetDrawerLayout {
  count: number;
  frontHeightMm: number;
  /** Все высоты — от низа корпуса (пола), мм. */
  frontsBottomMm: number;
  frontsTopMm: number;
  /** Неподвижная полка над ящиками: низ и верх. */
  shelfBottomMm: number;
  shelfTopMm: number;
  /** Низ корпуса изнутри: верх дна. */
  carcassFloorMm: number;
  /** Нижняя кромка дверей над ящиками. */
  doorBottomMm: number;
  bays: CabinetDrawerBay[];
}

/** Нижний край фасада ящика номер index (снизу вверх), мм от пола. */
export const cabinetDrawerFrontBottomMm = (layout: CabinetDrawerLayout, index: number) =>
  layout.frontsBottomMm + index * (layout.frontHeightMm + CABINET_DRAWER_SEAM_MM);

export const cabinetDrawerLayout = (module: WardrobeModule): CabinetDrawerLayout | null => {
  const count = cabinetDrawerCount(module);
  if (!count) return null;
  const frontHeightMm = cabinetDrawerFrontHeightMm(module);
  const base = cabinetBaseHeightMm(module);
  const frontsBottomMm = base + FRONT_BOTTOM_REVEAL_MM;
  const frontsTopMm = frontsBottomMm + count * frontHeightMm + (count - 1) * CABINET_DRAWER_SEAM_MM;
  // Полка — за зазором между верхним ящиком и дверью, по его середине: её кромка видна в зацепе.
  const shelfCenter = frontsTopMm + CABINET_DRAWER_SEAM_MM / 2;
  const sections = cabinetSectionWidths(module.width);
  let cursor = -module.width / 2;
  const bays = sections.map((width, sectionIndex) => {
    const start = cursor;
    const end = cursor + width;
    cursor = end;
    const first = sectionIndex === 0;
    const last = sectionIndex === sections.length - 1;
    return {
      sectionIndex,
      frontLeft: start + (first ? 2 : 1),
      frontRight: end - (last ? 2 : 1),
      innerLeft: start + (first ? PANEL_MM : PANEL_MM / 2),
      innerRight: end - (last ? PANEL_MM : PANEL_MM / 2)
    };
  });
  return {
    count,
    frontHeightMm,
    frontsBottomMm,
    frontsTopMm,
    shelfBottomMm: shelfCenter - PANEL_MM / 2,
    shelfTopMm: shelfCenter + PANEL_MM / 2,
    carcassFloorMm: base + PANEL_MM,
    doorBottomMm: frontsTopMm + CABINET_DRAWER_SEAM_MM,
    bays
  };
};

/** Верх неподвижной полки над ящиками — дно для наполнения шкафа; без ящиков — верх дна. */
export const cabinetInteriorFloorMm = (module: WardrobeModule) => {
  if (module.type === "mezzanine") return PANEL_MM;
  return cabinetDrawerLayout(module)?.shelfTopMm ?? cabinetBaseHeightMm(module) + PANEL_MM;
};

/**
 * От чего отсчитывается дверь: низ корпуса (цоколь) или шов над наружными
 * ящиками. Нижняя кромка двери на 2 мм выше этого уровня.
 */
export const cabinetDoorBaseMm = (module: WardrobeModule) => {
  if (module.type === "mezzanine") return 0;
  const layout = cabinetDrawerLayout(module);
  return layout ? layout.doorBottomMm - FRONT_BOTTOM_REVEAL_MM : cabinetBaseHeightMm(module);
};

/**
 * Высота створки, от которой считаются ручка и её поля: «Центр от низа
 * фасада» отсчитывается от нижней кромки двери, а над наружными ящиками
 * она выше. Антресоль — как было: от высоты минус основание.
 */
export const handleDoorHeightMm = (module: WardrobeModule) => Math.max(
  120,
  module.height - (module.type === "mezzanine" ? cabinetBaseHeightMm(module) : cabinetDoorBaseMm(module))
);

/** Площадь фасадов наружных ящиков, м². */
export const cabinetDrawerFrontsAreaM2 = (layout: CabinetDrawerLayout) =>
  layout.bays.reduce((sum, bay) => sum + (bay.frontRight - bay.frontLeft) * layout.frontHeightMm * layout.count, 0) / 1_000_000;
